import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { GripVertical, Trash2, ArrowUp, ArrowDown, Eye } from "lucide-react";
import {
  ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator,
} from "../ui/context-menu";
import SheetBlock from "./SheetBlock";
import MetaAdsBlock from "./MetaAdsBlock";
import TicketSalesBlock from "./TicketSalesBlock";
import CurrentRoasBlock from "./CurrentRoasBlock";
import { TextBlock, LinkBlock, EmbedBlock, ChecklistBlock, KVStatusBlock, FileBlock, MediaBlock } from "./SimpleBlocks";

function timeAgo(iso) {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function ReadReceipt({ blockId, isTeam }) {
  const [views, setViews] = useState([]);
  useEffect(() => {
    if (!isTeam) return;
    let cancelled = false;
    api.get(`/blocks/${blockId}/views`).then((r) => { if (!cancelled) setViews(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [blockId, isTeam]);

  if (!isTeam) return null;
  if (views.length === 0) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-[10px] text-zinc-700 font-mono uppercase tracking-wider">
        <Eye className="w-2.5 h-2.5" /> Not viewed yet
      </div>
    );
  }
  const latest = views.reduce((acc, v) => (!acc || v.last_viewed_at > acc.last_viewed_at ? v : acc), null);
  return (
    <div className="mt-2 flex items-center gap-1.5 text-[10px] text-zinc-500 font-mono">
      <Eye className="w-2.5 h-2.5" />
      <span className="uppercase tracking-wider">Viewed by {latest.user_name} · {timeAgo(latest.last_viewed_at)}</span>
    </div>
  );
}

export default function BlockRenderer({
  block, isTeam, onUpdateBlock, onDeleteBlock, onMove, onNavigate,
  onDragStart, onDragOver, onDrop, onDragEnd, isDragging, dragOver,
}) {
  const { user } = useAuth();
  const [dragOK, setDragOK] = useState(false);

  // Mark as viewed (clients only) once per mount
  useEffect(() => {
    if (user && user.role === "client") {
      api.post(`/blocks/${block.id}/view`).catch(() => {});
    }
  }, [block.id, user]);

  const renderInner = () => {
    const props = { block, isTeam, onUpdateBlock };
    switch (block.type) {
      case "text": return <TextBlock {...props} />;
      case "sheet": return <SheetBlock {...props} />;
      case "meta_ads": return <MetaAdsBlock {...props} />;
      case "ticket_sales": return <TicketSalesBlock {...props} />;
      case "roas": return <CurrentRoasBlock {...props} />;
      case "link": return <LinkBlock {...props} onNavigate={onNavigate} />;
      case "file": return <FileBlock {...props} />;
      case "embed": return <EmbedBlock {...props} />;
      case "media": return <MediaBlock {...props} />;
      case "checklist": return <ChecklistBlock {...props} />;
      case "kv": return <KVStatusBlock {...props} />;
      default: return <div className="text-zinc-600 text-[12px]">Unknown block type: {block.type}</div>;
    }
  };

  const overClass = dragOver === "top" ? "drag-over-top" : dragOver === "bottom" ? "drag-over-bottom" : "";

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild disabled={!isTeam}>
        <div
          className={`block-wrap group relative ${isDragging ? "is-dragging" : ""} ${overClass}`}
          data-testid={`block-${block.id}`}
          id={`block-${block.id}`}
          draggable={isTeam && dragOK}
          onDragStart={(e) => onDragStart && onDragStart(e, block.id)}
          onDragOver={(e) => onDragOver && onDragOver(e, block.id)}
          onDrop={(e) => onDrop && onDrop(e, block.id)}
          onDragEnd={(e) => { setDragOK(false); onDragEnd && onDragEnd(e); }}
          onMouseUp={() => setDragOK(false)}
        >
          {isTeam && (
            <div className="block-handle"
              onMouseDown={() => setDragOK(true)}
              onMouseUp={() => setDragOK(false)}>
              <button title="Drag to reorder" className="w-5 h-5 rounded hover:bg-zinc-800 text-zinc-600 hover:text-zinc-200 flex items-center justify-center cursor-grab active:cursor-grabbing">
                <GripVertical className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {renderInner()}
          <ReadReceipt blockId={block.id} isTeam={isTeam} />
        </div>
      </ContextMenuTrigger>
      {isTeam && (
        <ContextMenuContent className="bg-[#111] border-zinc-800 text-[12px] min-w-[180px]">
          <ContextMenuItem onClick={() => onMove(block.id, -1)} className="cursor-pointer">
            <ArrowUp className="w-3.5 h-3.5 mr-2" /> Move up
          </ContextMenuItem>
          <ContextMenuItem onClick={() => onMove(block.id, 1)} className="cursor-pointer">
            <ArrowDown className="w-3.5 h-3.5 mr-2" /> Move down
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onClick={() => onDeleteBlock(block.id)} className="cursor-pointer text-[#EF4444] focus:text-[#EF4444]">
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete block
          </ContextMenuItem>
        </ContextMenuContent>
      )}
    </ContextMenu>
  );
}
